import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../components/ui/Card'; // Import Card component
import Input from '../components/ui/Input'; // Import Input component
import Button from '../components/ui/Button'; // Import Button component
import FastAPIClient from '../services/apiClient'; // Assuming this is the client
import logo_1024_X_1024 from '../assets/logo_1024_X_1024.png'; // Import the logo

/**
 * Reset Password Page for the application.
 * Allows users to reset their password using a salt/token from a reset link.
 * Handles client-side validation and interacts with the FastAPI backend.
 * Designed with Tailwind CSS, adhering to the specified design principles.
 */
const ResetPasswordPage = () => {
    const { salt } = useParams(); // This 'salt' is likely a reset token
    const navigate = useNavigate();
    const client = new FastAPIClient(); // Instantiate the client

    useEffect(() => {
        // Clear local storage on component mount, as per original logic
        localStorage.clear();
    }, []);

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [passwordsMatchError, setPasswordsMatchError] = useState(false);
    const [passwordFormatError, setPasswordFormatError] = useState('');

    const validatePassword = (pwd) => {
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+=\-\[\]{};':"\\|,.<>\/?`~])[A-Za-z\d!@#$%^&*()_+=\-\[\]{};':"\\|,.<>\/?`~]{8,}$/;
        if (!pwd) return 'Password is required.';
        if (!passwordRegex.test(pwd)) {
            return 'Password must be at least 8 characters, include uppercase, lowercase, number, and special character.';
        }
        return '';
    };

    const handlePasswordChange = (e) => {
        const newPassword = e.target.value;
        setPassword(newPassword);
        setPasswordFormatError(validatePassword(newPassword));
        if (passwordsMatchError && newPassword === confirmPassword) {
            setPasswordsMatchError(false); // Clear mismatch error if they now match
        }
    };

    const handleConfirmPasswordChange = (e) => {
        const newConfirmPassword = e.target.value;
        setConfirmPassword(newConfirmPassword);
        if (passwordFormatError && newConfirmPassword === password) {
            setPasswordFormatError(''); // Clear format error if they now match (and format is good)
        }
        if (passwordsMatchError && newConfirmPassword === password) {
            setPasswordsMatchError(false); // Clear mismatch error if they now match
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setMessage('');
        setError(false);
        setPasswordsMatchError(false);
        setPasswordFormatError('');

        const formatError = validatePassword(password);
        if (formatError) {
            setPasswordFormatError(formatError);
            setError(true);
            return;
        }

        if (password !== confirmPassword) {
            setPasswordsMatchError(true);
            setMessage('Passwords do not match.');
            setError(true);
            return;
        }

        setLoading(true);

        try {
            // Assuming 'confirm-invite' endpoint is repurposed for password reset with a token (salt)
            // If there's a specific reset password endpoint, this should be updated.
            await client.postData(`/auth/change-password`, { salt, password });
            setMessage('Password reset successfully! Redirecting to login...');
            setError(false);
            setTimeout(() => {
                navigate('/login');
            }, 2000);

        } catch (err) {
            console.error('Password reset error:', err);
            const errorMessage = err?.response?.data?.detail || err?.response?.data?.message || 'An unexpected error occurred. Please try again.';
            setMessage(`Error: ${errorMessage}`);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-light p-4">
            <Card className="w-full max-w-md mx-auto p-8">
                {/* Logo strategically placed at the top of the card */}
                <div className="flex justify-center mb-6">
                    <img
                        src={logo_1024_X_1024}
                        alt="Arxis Logo"
                        className="w-24 h-24 object-contain rounded-lg select-none pointer-events-none"
                        onContextMenu={(e) => e.preventDefault()}
                        draggable="false"
                    />
                </div>

                <h2 className="text-2xl font-bold text-center text-text-primary mb-6">
                    Reset Your Password
                </h2>
                <p className="text-center text-text-secondary mb-6 text-sm">
                    Enter and confirm your new password below.
                </p>

                {message && (
                    <div className={`p-3 mb-4 rounded-lg text-sm text-center
                        ${error ? 'bg-gmail-red-light text-gmail-red' : 'bg-gmail-green-light text-gmail-green'}`}>
                        {message}
                    </div>
                )}

                <form onSubmit={handleSubmit} noValidate className="space-y-4">
                    <Input
                        label="New Password"
                        id="password"
                        name="password"
                        type="password"
                        placeholder="Enter your new password"
                        value={password}
                        onChange={handlePasswordChange}
                        required
                        error={passwordFormatError || (passwordsMatchError ? ' ' : '')} // Pass empty string for error if only mismatch
                    />
                    {passwordFormatError && <p className="text-gmail-red text-xs mt-[-10px] mb-4">{passwordFormatError}</p>}


                    <Input
                        label="Confirm Password"
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        placeholder="Confirm your new password"
                        value={confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        required
                        error={passwordsMatchError ? 'Passwords do not match.' : ''}
                    />
                    {passwordsMatchError && (
                        <p className="text-gmail-red text-xs mt-[-10px] mb-4">
                            Passwords do not match.
                        </p>
                    )}

                    <Button
                        type="submit"
                        fullWidth
                        disabled={loading}
                        className="mt-6"
                    >
                        {loading ? (
                            <div className="flex items-center justify-center">
                                <svg className="animate-spin h-5 w-5 text-white mr-3" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Resetting Password...
                            </div>
                        ) : (
                            'Reset Password'
                        )}
                    </Button>
                </form>
            </Card>
        </div>
    );
};

export default ResetPasswordPage;
